import { useContext, memo } from 'react';
import { Button } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import DataTable from '../../../DataTable/datatable.component';
import DateColumn from '../../../DataTable/datatable.datecolumn';
import { Link } from 'react-router-dom';
import ConfigurationContext from '../../../Contexts/ConfigurationContext';
import { LotDetailContext } from '../../../Contexts/LotDetailContext';
import { QIR } from '../../../Types/QIR';

interface QIRTableProps {
    qirs: QIR[],
    columns: QirColumnType[];
}

export type QirColumnType = "IncidentId"
    | "LotNumber"
    | "Status"
    | "Category"
    | "Subcategory"
    | "IncidentDate"
    | "EndDate"
    | "Statement"
    | "ReportedBy"
    | "ReportedDate"
    | "ClientQIRId"
    | "GradingAction"
    | "GradingActionNotes"
    | "LotSplitInfo"

function QIRTable({ qirs, columns }: QIRTableProps) {

    const { t } = useTranslation();

    const qirColumnTypeToColumn = (columnType: QirColumnType): any => {
        switch (columnType) {
            case "IncidentId":
                return {
                    Header: t('LotDetails.QIRs.IncidentId'),
                    accessor: 'incidentId',
                    filter: 'includesString',
                    Cell: (props: any) => {
                        let qir = props.row.original;

                        if (qir.url !== undefined && qir.url !== "") {
                            return <Link to={qir.url} target="_blank">{props.value}</Link>
                        }

                        return props.value;
                    },
                    width: 100
                };
            case "LotNumber":
                return {
                    Header: t('LotDetails.QIRs.LotNumber'),
                    accessor: 'lotNumber',
                    filter: 'includesString',
                    Cell: (props: any) => <Link to={`/lotdetails/${props.value}`}>{props.value}</Link>,
                    width: 100
                };
            case "Status":
                return {
                    Header: t('LotDetails.QIRs.Status'),
                    accessor: 'status',
                    filter: 'includesString',
                    width: 100
                };
            case "Category":
                return {
                    Header: t('LotDetails.QIRs.Category'),
                    accessor: 'Category',
                    filter: 'includesString',
                    Cell: (props: any) => {
                        let qir = props.row.original;
                        return (
                            <div className="text-break">
                                {
                                    qir.category &&
                                    <p style={{ whiteSpace: 'pre-wrap' }}>{qir.category}</p>
                                }
                            </div>
                        );
                    }
                };
            case "Subcategory":
                return {
                    Header: t('LotDetails.QIRs.Subcategory'),
                    accessor: 'subcategory',
                    filter: 'includesString',
                    Cell: (props: any) => {
                        let qir = props.row.original;
                        return (
                            <div className="text-break">
                                {qir.subcategory && qir.subcategory.length > 0 &&
                                    <p style={{ whiteSpace: 'pre-wrap' }}>{qir.subcategory.join("\n")}</p>
                                }
                            </div>
                        );
                    }
                };
            case "IncidentDate":
                return DateColumn({ header: t('LotDetails.QIRs.IncidentDate'), accessor: 'incidentDateUtc' });
            case "EndDate":
                return DateColumn({ header: t('LotDetails.QIRs.EndDate'), accessor: 'endDateUtc' });
            case "Statement":
                return {
                    Header: t('LotDetails.QIRs.Statement'),
                    accessor: 'statement',
                    filter: 'includesString',
                    width: 300
                };
            case "ReportedBy":
                return {
                    Header: t('LotDetails.QIRs.ReportedBy'),
                    accessor: 'reportedBy',
                    filter: 'includesString',
                    width: 100
                };
            case "ReportedDate":
                return DateColumn({ header: t('LotDetails.QIRs.ReportedDate'), accessor: 'reportedDateUtc' })
            case "ClientQIRId":
                return {
                    Header: t('LotDetails.QIRs.ClientQIRId'),
                    accessor: 'clientQirId',
                    filter: 'includesString'
                };
            case "GradingAction":
                return {
                    Header: t('LotDetails.QIRs.GradingAction'),
                    accessor: 'gradingAction',
                    filter: 'includesString'
                };
            case "GradingActionNotes":
                return {
                    Header: t('LotDetails.QIRs.GradingActionNotes'),
                    accessor: 'gradingActionNotes',
                    filter: 'includesString',
                    width: 300
                };
            case "LotSplitInfo":
                return {
                    Header: t('LotDetails.QIRs.LotSplitInfo'),
                    accessor: 'lotSplitInfo',
                    filter: 'includesString',
                    Cell: (props: any) => {
                        let qir = props.row.original;

                        return <p>{qir.lotSplitRequired ? qir.lotSplitInfo : "Not required"}</p>
                    }
                };
        }
    };

    var tableColumns = columns.map(col => qirColumnTypeToColumn(col));

    return qirs.length > 0
        ? <DataTable tableColumns={tableColumns} tableData={qirs} showPaging={false} />
        : <span>{t('CommonResponses.NoData')}</span>;
};

interface CreateQIRButtonProps {
    businessUnit: string,
    lotNumber: string,
    line: string,
    grade: string,
}

function CreateQIRButton({ businessUnit, lotNumber, line, grade }: CreateQIRButtonProps) {

    const { t } = useTranslation();

    const { configurationData } = useContext(ConfigurationContext);

    let qirUrlConfig = configurationData?.find((x: any) => x.name === "QIRMS.CreateQIRUrl");
    if (qirUrlConfig) {

        const qirUrl = qirUrlConfig.value + `?businessUnit=${businessUnit}&lotNumber=${lotNumber}&line=${line}&grade=${grade}`;

        return (
            <Link to={qirUrl} target="_blank">
                <Button>{t('LotDetails.QIRs.CreateQIR')}</Button>
            </Link>
        );
    }
    else {
        return <></>
    }
};

interface QIRSection_QIRMSProps {
    showCreateQIRButton: boolean,
    customColumns?: QirColumnType[]
}

const QIRSection_QIRMS = memo(function QIRSection_QIRMS({ showCreateQIRButton, customColumns = [] }: QIRSection_QIRMSProps) {
    const {
        lotDetail: {
            summary: {
                lotNumber,
                businessUnitCode,
                grade,
                productionLine: line
            },
            qirs
        }
    } = useContext(LotDetailContext);

    const defaultColumns: QirColumnType[] = [
        "IncidentId",
        "LotNumber",
        "Status",
        "Category",
        "IncidentDate",
        "EndDate",
        "Statement",
        "ReportedBy",
        "ReportedDate",
        "ClientQIRId"
    ];

    const qirColumns = customColumns.length > 0 ? customColumns : defaultColumns;

    return (
        <>
            <QIRTable qirs={qirs} columns={qirColumns} />
            {showCreateQIRButton &&
                <div className="d-flex justify-content-end">
                    <CreateQIRButton businessUnit={businessUnitCode} lotNumber={lotNumber} line={line} grade={grade} />
                </div>
            }
        </>
    );
});

export default QIRSection_QIRMS;